// src/components/atoms/MultiSelect/MultiSelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import * as styles from '../Dropdown/Dropdown.css';
import clsx from 'clsx';
import { Icon } from 'icons';

export interface MultiSelectOption {
  value: string | number;
  label: string;
}

export interface MultiSelectProps {
  /** 아이템 목록 */
  options?: MultiSelectOption[];
  /** 아무것도 선택되지 않았을 때 보여줄 텍스트 */
  placeholder?: string;
  /** 선택 값이 바뀔 때 호출되는 콜백 */
  onChange?: (value: (string | number)[]) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  className?: string;
  /** 외부에서 제어하는 선택값 (선택) */
  value?: (string | number)[] | null;
  /** 선택 해제 허용 */
  allowClear?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  placeholder = '선택해주세요.',
  onChange,
  disabled = false,
  className,
  value = undefined,
  allowClear = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<(string | number)[] | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 전역 DOM MouseEvent 타입을 사용합니다.
    const handleClick = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleSelect = (val: string | number) => {
    const currentValues = (typeof value === 'undefined' ? selected : value) as (string | number)[] | null;
    const currentArray = Array.isArray(currentValues) ? currentValues : [];
    const isSelected = currentArray.includes(val);
    
    let newValues: (string | number)[];
    if (isSelected) {
      newValues = currentArray.filter(v => v !== val);
    } else {
      newValues = [...currentArray, val];
    }
    
    // 외부 제어 모드가 아니면 내부 상태 업데이트
    if (typeof value === 'undefined') {
      setSelected(newValues.length > 0 ? newValues : null);
    }
    onChange?.(newValues.length > 0 ? newValues : []);
  };

  const currentValue = typeof value === 'undefined' ? selected : value;
  const values = Array.isArray(currentValue) ? currentValue : [];
  
  // 선택된 항목 제거
  const handleRemove = (val: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newValues = values.filter(v => v !== val);
    if (typeof value === 'undefined') {
      setSelected(newValues.length > 0 ? newValues : null);
    }
    onChange?.(newValues.length > 0 ? newValues : []);
  };
  
  // 선택된 라벨 표시 (버튼 내부)
  const getSelectedLabel = () => {
    if (values.length === 0) return placeholder;
    return `${values.length}개 선택됨`;
  };
  
  const selectedLabel = getSelectedLabel();
  
  // 선택 여부 확인
  const isSelected = (val: string | number) => {
    return values.includes(val);
  };

  // 선택된 항목들의 라벨 가져오기
  const getSelectedItems = () => {
    return values.map(val => {
      const option = options.find(o => o.value === val);
      return option ? { value: val, label: option.label } : null;
    }).filter(Boolean) as { value: string | number; label: string }[];
  };

  return (
    <div className={clsx(styles.wrapper, className)} ref={ref}>
        <div className='relative'>
            <button
                type="button"
                className={styles.button}
                onClick={() => setOpen((prev) => !prev)}
                disabled={disabled}
            >
                {selectedLabel}
                <Icon
                name="arrowDown"
                size={24}
                style={{
                    position: 'absolute',
                    right: 16,
                    top: '50%',
                    transform: open
                    ? 'translateY(-50%) rotate(180deg)'
                    : 'translateY(-50%) rotate(0deg)',
                }}
                />
            </button>
            <div className={clsx(styles.menu, open && styles.menuVisible, 'absolute !top-[100%] left-0 w-full')}>
        {options.length > 0 ? (
          options.map((opt) => (
            <div
              key={opt.value}
              className={styles.menuItem}
              data-selected={isSelected(opt.value) ? true : undefined}
              onClick={() => handleSelect(opt.value)}
            >
              <span className="">
                {isSelected(opt.value) ? '✓ ' : ''}
              </span>
              {opt.label}
            </div>
          ))
        ) : (
          <div className={styles.menuItem} style={{ cursor: 'default' }}>
            옵션이 없습니다
          </div>
        )}
      </div>
        </div>

      {/* 선택된 항목 태그들 */}
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {getSelectedItems().map((item) => (
            <span
              key={item.value}
              className="inline-flex gap-1 items-center px-2 py-1 text-sm text-gray-700 bg-gray-100 rounded-md"
            >
              {item.label}
              <button
                type="button"
                onClick={(e) => handleRemove(item.value, e)}
                className="flex justify-center items-center w-4 h-4 text-gray-500 hover:text-gray-700"
                disabled={disabled}
              >
                <Icon name="close" size={14} className='*:fill-gray-900' />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

